import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Config } from '../app.configs';
import { PaginatedBlacklistUsdcWallets } from '../interfaces/paginated-blacklist-usdc-wallet.interface';
import { BlacklistUsdcWallet } from '../models/blacklist-usdc-wallet';
import { PaginatedBlacklistIbans } from '../interfaces/paginated-blacklist-ibans.interface';
import { BlacklistIban } from '../models/blacklist-iban.model';
import { PaginatedBlacklistPhoneNumbers } from '../interfaces/paginated-blacklist-phone-numbers.interface';
import { BlacklistPhoneNumber } from '../models/blacklist-phone-number.model';
import { PaginatedBlacklistUsers } from '../interfaces/paginated-blacklist-users.interface';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root',
})
export class BlacklistService {
  private readonly CACHE_PREFIX_USDC = 'blacklist-usdc:';
  private readonly CACHE_PREFIX_IBAN = 'blacklist-iban:';
  private readonly CACHE_PREFIX_PHONE = 'blacklist-phone:';
  private readonly CACHE_PREFIX_USERS = 'blacklist-users:';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private cacheService: CacheService,
  ) {}

  getBlacklistUsdcWallets(
    page: number = 1,
    search: string = '',
    forceRefresh: boolean = false,
  ): Observable<PaginatedBlacklistUsdcWallets> {
    const cacheKey = `${this.CACHE_PREFIX_USDC}${page}:${search}`;

    return this.cacheService.get<PaginatedBlacklistUsdcWallets>(
      cacheKey,
      () => {
        const params = new HttpParams().set('page', page).set('search', search);
        const headers = this.authService.getHeaders();
        return this.http.get<PaginatedBlacklistUsdcWallets>(
          Config.toApiUrl(`dashboard/blacklist-usdc-wallets/`),
          { headers, params, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  invalidateUsdcCache(): void {
    this.cacheService.invalidateByPrefix(this.CACHE_PREFIX_USDC);
  }

  addBlacklistedUsdcAddress(address: string): Observable<BlacklistUsdcWallet> {
    const headers = this.authService.getHeaders();
    return this.http
      .post<BlacklistUsdcWallet>(
        Config.toApiUrl('dashboard/blacklist-usdc-wallets/'),
        { address },
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateUsdcCache()));
  }

  deleteBlacklistedUsdcAddress(id: string): Observable<any> {
    const headers = this.authService.getHeaders();
    return this.http
      .delete(Config.toApiUrl(`dashboard/blacklist-usdc-wallets/${id}/`), {
        headers,
        withCredentials: true,
      })
      .pipe(tap(() => this.invalidateUsdcCache()));
  }

  getBlacklistIbans(
    page: number = 1,
    search: string = '',
    forceRefresh: boolean = false,
  ): Observable<PaginatedBlacklistIbans> {
    const cacheKey = `${this.CACHE_PREFIX_IBAN}${page}:${search}`;

    return this.cacheService.get<PaginatedBlacklistIbans>(
      cacheKey,
      () => {
        const params = new HttpParams().set('page', page).set('search', search);
        const headers = this.authService.getHeaders();
        return this.http.get<PaginatedBlacklistIbans>(
          Config.toApiUrl(`dashboard/blacklist-ibans/`),
          { headers, params, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  invalidateIbanCache(): void {
    this.cacheService.invalidateByPrefix(this.CACHE_PREFIX_IBAN);
  }

  addBlacklistedIban(iban: string): Observable<BlacklistIban> {
    const headers = this.authService.getHeaders();
    return this.http
      .post<BlacklistIban>(
        Config.toApiUrl('dashboard/blacklist-ibans/'),
        { iban },
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateIbanCache()));
  }

  deleteBlacklistedIban(id: string): Observable<any> {
    const headers = this.authService.getHeaders();
    return this.http
      .delete(Config.toApiUrl(`dashboard/blacklist-ibans/${id}/`), {
        headers,
        withCredentials: true,
      })
      .pipe(tap(() => this.invalidateIbanCache()));
  }

  getBlacklistPhoneNumbers(
    page: number = 1,
    search: string = '',
    forceRefresh: boolean = false,
  ): Observable<PaginatedBlacklistPhoneNumbers> {
    const cacheKey = `${this.CACHE_PREFIX_PHONE}${page}:${search}`;

    return this.cacheService.get<PaginatedBlacklistPhoneNumbers>(
      cacheKey,
      () => {
        const params = new HttpParams().set('page', page).set('search', search);
        const headers = this.authService.getHeaders();
        return this.http.get<PaginatedBlacklistPhoneNumbers>(
          Config.toApiUrl(`dashboard/blacklist-phone-numbers/`),
          { headers, params, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  invalidatePhoneCache(): void {
    this.cacheService.invalidateByPrefix(this.CACHE_PREFIX_PHONE);
  }

  addBlacklistedPhoneNumber(
    phone_number: string,
  ): Observable<BlacklistPhoneNumber> {
    const headers = this.authService.getHeaders();
    return this.http
      .post<BlacklistPhoneNumber>(
        Config.toApiUrl('dashboard/blacklist-phone-numbers/'),
        { phone_number },
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidatePhoneCache()));
  }

  deleteBlacklistedPhoneNumber(id: string): Observable<any> {
    const headers = this.authService.getHeaders();
    return this.http
      .delete(Config.toApiUrl(`dashboard/blacklist-phone-numbers/${id}/`), {
        headers,
        withCredentials: true,
      })
      .pipe(tap(() => this.invalidatePhoneCache()));
  }

  getBlacklistUsers(
    page: number = 1,
    search: string = '',
    forceRefresh: boolean = false,
  ): Observable<PaginatedBlacklistUsers> {
    const cacheKey = `${this.CACHE_PREFIX_USERS}${page}:${search}`;

    return this.cacheService.get<PaginatedBlacklistUsers>(
      cacheKey,
      () => {
        const params = new HttpParams().set('page', page).set('search', search);
        const headers = this.authService.getHeaders();
        return this.http.get<PaginatedBlacklistUsers>(
          Config.toApiUrl(`dashboard/users/12345/blacklist/`),
          { headers, params, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  invalidateUsersCache(): void {
    this.cacheService.invalidateByPrefix(this.CACHE_PREFIX_USERS);
  }

  blacklistUser(uuid: string, reason: string): Observable<any> {
    const headers = this.authService.getHeaders();
    return this.http
      .post<any>(
        Config.toApiUrl(`dashboard/users/${uuid}/blacklist/`),
        { reason },
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateUsersCache()));
  }
}
